// @ts-nocheck
import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle, FileCheck, Users, DollarSign, Calculator } from 'lucide-react';
import { PageHeader, Card, Button, InfoBanner, Badge, StatCard } from '../../components/WorkflowFormPrimitives';
import { fmt } from '../../utils/formatCurrency';
import { build280gAnalysisResults, excessForRow } from '../../utils/build280gAnalysisResults';
import { useCompanyContext } from '../../context/CompanyContext';
import { isCompensationStepComplete, isBaseAmountStepComplete } from '../../../../utils/compensationStep';
import { hasParachuteProgress } from '../../components/WorkflowStepsPanel';
import { DEFAULT_DOCUMENTS_WORKFLOW_SUB_ID } from '../../documentsWorkflowSubItems';
import { VoteRoom280FullExperience } from '../shareholderApproval/voteRoom';
import { getAnalysisDocuments } from '../../../../services/AnalysisApiClient';
import { DocumentsTable } from './DocumentsTable';
import { WaiverAgreementsPanel } from './WaiverAgreementsPanel';
import { VoteTallyBanner } from './VoteTallyBanner';

const SUB_TAB_TITLES = {
  generate: {
    title: 'Documents',
    subtitle: 'Generate, Review, and Download Shareholder Approval Documents',
  },
  waiver: {
    title: '280G Waiver Agreements',
    subtitle: 'Individuals Over the Threshold and Waivers Required for Shareholder Approval',
  },
  voteRoom: {
    title: 'Vote Room',
    subtitle: 'Run the Shareholder Vote and Capture Results in One Place',
  },
};

export const DocumentGeneration = ({ formData, subTab = DEFAULT_DOCUMENTS_WORKFLOW_SUB_ID }) => {
  const activeSubTab = SUB_TAB_TITLES[subTab] ? subTab : DEFAULT_DOCUMENTS_WORKFLOW_SUB_ID;

  const { currentAnalysis, navigateTo } = useCompanyContext();

  const results = build280gAnalysisResults(formData);
  const excessIndividuals = results.filter((r) => r.hasIssue);
  const totalExcess = results.reduce((s, r) => s + excessForRow(r), 0);

  const formatLastRunAt = (raw: unknown) => {
    if (raw == null) return null;
    const d = new Date(String(raw));
    if (Number.isNaN(d.getTime())) return String(raw);
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
  };

  // Documents are tied to the 280G run snapshot. If workflow was saved after the last run (or never run),
  // disable preview/generate to prevent mismatched documents.
  const isRunStale = useMemo(() => {
    const lr = currentAnalysis?.lastRunAt;
    const wf = currentAnalysis?.workflowLastChangedAt;
    if (!wf) return false;
    if (!lr) return true;
    const tLast = new Date(String(lr)).getTime();
    const tWf = new Date(String(wf)).getTime();
    if (Number.isNaN(tLast) || Number.isNaN(tWf)) return false;
    return tLast < tWf;
  }, [currentAnalysis?.lastRunAt, currentAnalysis?.workflowLastChangedAt]);

  const runDisabled =
    !formData?.companyName ||
    (formData?.individuals?.length ?? 0) <= 0 ||
    !isCompensationStepComplete(formData || {}) ||
    !isBaseAmountStepComplete(formData || {}) ||
    !hasParachuteProgress(formData || {});

  const lastRunLabel = formatLastRunAt(currentAnalysis?.lastRunAt);
  const isRunFresh = currentAnalysis?.lastRunAt != null && !isRunStale;
  const runButtonDisabled = runDisabled || isRunFresh;
  const shouldMaskDocuments = currentAnalysis?.lastRunAt == null || isRunStale;

  // Vote Room is hidden behind the mask only in the truly empty state — no 280G run AND
  // no generated documents. Once either exists the operator should be able to open it.
  const noRunYet = currentAnalysis?.lastRunAt == null;
  const [hasAnyDocument, setHasAnyDocument] = useState<boolean>(false);
  useEffect(() => {
    let cancelled = false;
    const id = currentAnalysis?.id;
    if (!id) {
      setHasAnyDocument(false);
      return () => {
        cancelled = true;
      };
    }
    void (async () => {
      try {
        const docs = await getAnalysisDocuments(id, { latestOnly: true });
        if (!cancelled) setHasAnyDocument(Array.isArray(docs) && docs.length > 0);
      } catch {
        if (!cancelled) setHasAnyDocument(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentAnalysis?.id, currentAnalysis?.lastRunAt]);

  const voteRoomShouldMask = noRunYet && !hasAnyDocument;
  const maskStaleShareholderDocs = activeSubTab === 'voteRoom' ? voteRoomShouldMask : shouldMaskDocuments;

  return (
    <div className="space-y-5">
      <PageHeader
        title={
          <span className="inline-flex items-center gap-3">
            <span>{SUB_TAB_TITLES[activeSubTab].title}</span>
            {currentAnalysis?.lastRunAt || currentAnalysis?.workflowLastChangedAt ? (
              <Badge variant={isRunStale ? 'danger' : 'info'}>Last Run: {lastRunLabel ?? '—'}</Badge>
            ) : null}
          </span>
        }
        subtitle={SUB_TAB_TITLES[activeSubTab].subtitle}
        action={
          <div className="flex items-center gap-2">
            <Button
              variant="primary"
              size="sm"
              disabled={runButtonDisabled}
              onClick={() => navigateTo('analysis')}
              title={isRunFresh ? 'Latest run already matches current workflow changes.' : undefined}
            >
              <Calculator size={14} className="mr-1.5" /> Run 280G Results
            </Button>
          </div>
        }
      />

      <div className="relative">
        <div
          className={
            maskStaleShareholderDocs ? 'space-y-5 blur-[2px] opacity-60 pointer-events-none select-none' : 'space-y-5'
          }
        >
          {/* Status Banner */}
          {excessIndividuals.length > 0 ? (
            <div className="border border-amber-200 bg-amber-50 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <AlertTriangle className="text-amber-600 mt-0.5 flex-shrink-0" size={18} />
                <div>
                  <h4 className="text-xs font-medium text-amber-900">Shareholder Approval Required</h4>
                  <p className="text-xs text-amber-800 mt-1">
                    {excessIndividuals.length === 1
                      ? '1 individual exceeds'
                      : `${excessIndividuals.length} individuals exceed`}{' '}
                    the 280G threshold with {fmt(totalExcess)} in total excess payments. You must generate and
                    distribute the disclosure statement and obtain shareholder approval.
                  </p>
                </div>
              </div>
            </div>
          ) : results.length > 0 ? (
            <div className="border border-emerald-200 bg-emerald-50 rounded-lg p-4">
              <div className="flex items-center space-x-3">
                <CheckCircle className="text-emerald-600 flex-shrink-0" size={18} />
                <div>
                  <h4 className="text-xs font-medium text-emerald-900">No Shareholder Approval Required</h4>
                  <p className="text-xs text-emerald-700">
                    All individuals are under the 280G threshold. Disclosure documents are optional.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <InfoBanner variant="warning">
              Add disqualified individuals and complete the analysis to generate documents.
            </InfoBanner>
          )}

          {/* Summary Stats */}
          {results.length > 0 && (
            <div className="grid grid-cols-4 gap-3">
              <StatCard label="Total DQIs" value={results.length} icon={Users} />
              <StatCard label="Over Threshold" value={excessIndividuals.length} icon={AlertTriangle} />
              <StatCard label="Total Excess" value={fmt(totalExcess)} icon={DollarSign} />
              <StatCard label="Run on File" value={currentAnalysis?.lastRunId ? 'Yes' : 'No'} icon={FileCheck} />
            </div>
          )}

          {activeSubTab === 'generate' && (
            <>
              <InfoBanner variant="info" showIcon>
                Download and regenerate documents here. Send DQI waivers from the <strong>280G Waiver</strong> tab.
                Disclosure and consent are sent automatically to eligible shareholders when you launch the Vote Room.
              </InfoBanner>
              <VoteTallyBanner analysisId={currentAnalysis?.id ?? null} runId={currentAnalysis?.lastRunId ?? null} />
              <DocumentsTable
                analysisId={currentAnalysis?.id ?? null}
                runId={currentAnalysis?.lastRunId ?? null}
                generationDisabled={isRunStale || runDisabled}
                generationDisabledReason={
                  isRunStale
                    ? 'Run 280G Results after the latest workflow changes to regenerate.'
                    : runDisabled
                      ? 'Complete the workflow before generating documents.'
                      : undefined
                }
              />

              <Card className="bg-gray-50 border-gray-200">
                <h3 className="text-xs font-medium text-gray-900 mb-3">Shareholder Approval Requirements</h3>
                <div className="space-y-3 text-xs text-gray-600">
                  <div className="flex items-start space-x-2">
                    <CheckCircle size={14} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span>
                      <strong>75% approval required</strong> â€” More than 75% of voting shares (excluding DQIs
                      requiring shareholder approval for his or her payments) must approve
                    </span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <CheckCircle size={14} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span>
                      <strong>Adequate disclosure</strong> â€” All material facts about payments must be disclosed to
                      ALL shareholders entitled to vote
                    </span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <CheckCircle size={14} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span>
                      <strong>Waiver required</strong> â€” Each DQI must waive excess payments unless approved
                    </span>
                  </div>
                  <div className="flex items-start space-x-2">
                    <CheckCircle size={14} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                    <span>
                      <strong>Separate vote</strong> â€” Shareholders must vote separately on parachute payments (not
                      bundled with transaction)
                    </span>
                  </div>
                </div>
              </Card>
            </>
          )}

          {activeSubTab === 'waiver' &&
            (excessIndividuals.length > 0 ? (
              <WaiverAgreementsPanel
                analysisId={currentAnalysis?.id ?? null}
                runId={currentAnalysis?.lastRunId ?? null}
                excessIndividuals={excessIndividuals}
                formData={formData}
              />
            ) : (
              <Card>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="text-emerald-600 mt-0.5 flex-shrink-0" size={18} />
                  <div>
                    <h4 className="text-xs font-medium text-gray-900">No Waivers Required</h4>
                    <p className="text-xs text-gray-600 mt-1">
                      No individuals exceed the 280G threshold, so no parachute payment waivers are required.
                    </p>
                  </div>
                </div>
              </Card>
            ))}

          {activeSubTab === 'voteRoom' && <VoteRoom280FullExperience formData={formData} />}
        </div>

        {maskStaleShareholderDocs ? (
          <div className="absolute inset-0 z-10 flex items-start justify-center pt-24">
            <div className="px-3 py-1.5 rounded-md bg-red-50 border border-red-200 text-xs font-medium text-red-900 inline-flex items-center gap-1.5">
              <AlertTriangle size={14} className="text-red-700" />
              {activeSubTab === 'voteRoom' ? (
                <>
                  Click <span className="font-semibold">Run 280G Results</span> and generate documents before opening
                  the Vote Room.
                </>
              ) : (
                <>
                  Workflow changed. Click <span className="font-semibold">Run 280G Results</span> to refresh document
                  generation and waiver views.
                </>
              )}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};
